import React from 'react';
import { AlertCircle, Loader2, Lock } from 'lucide-react';

/** Purpose: Renders validating view. Inputs: none. Output: React render output (JSX). */
export function SharedAccessValidatingState() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
        <p className="text-slate-600">Validating share link...</p>
      </div>
    </div>
  );
}

/** Purpose: Renders error view. Inputs: error. Output: React render output (JSX). */
export function SharedAccessErrorState({ error }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="bg-red-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-red-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
        <p className="text-slate-600 mb-6">{error}</p>
        <p className="text-sm text-slate-500">
          If you believe this is an error, please contact the person who shared this link.
        </p>
      </div>
    </div>
  );
}

/** Purpose: Renders pin-required view. Inputs: pin state and handlers. Output: React render output (JSX). */
export function SharedAccessPinRequiredState({
  permissionInfo,
  pin,
  pinError,
  onPinChange,
  onSubmit,
  formatDate
}) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="bg-blue-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Lock className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">PIN Required</h1>
          <p className="text-slate-600">
            This shared content is protected. Enter the PIN provided by {permissionInfo?.ownerName || 'the owner'}.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          {pinError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
              {pinError}
            </div>
          )}

          <div>
            <input
              type="password"
              value={pin}
              onChange={(event) => onPinChange(event.target.value)}
              placeholder="Enter PIN"
              className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              maxLength={6}
              required
            />
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all"
          >
            Access Records
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-slate-200">
          <div className="text-sm text-slate-500 space-y-1">
            <p><strong>Shared by:</strong> {permissionInfo?.ownerName}</p>
            <p><strong>For:</strong> {permissionInfo?.grantedTo} at {permissionInfo?.organization}</p>
            <p><strong>Expires:</strong> {formatDate(permissionInfo?.expiresAt)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
